import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import "../../../../styles/lead-mangement/lms-pm-links.css";
import {
  SendIcon,
  SendCheckIcon,
  LeftArrowIcon,
} from "../../../../images/icons/icons";
import PaymentLinkForm from "./forms/PaymentLinkForm";
import DiscountPaymentForm from "./forms/DiscountPaymentForm";
import { fetchUserDetailsByMobileData } from "../../../../redux/reducers/apiSlice";
import { registerUser } from "../../../../redux/reducers/postApiSlice";
import { validatemap_form_email } from "../../../../exports/Validations";
import { useUpdateForm } from "../../../../data/Queries";
import { usersGetKey, userUpdate } from "../../../../data/ApiEndpoints";
import SelectDropdown from "../../../components-react/SelectDropdown";
import { gender } from "../../../common-data/DropdowData";
import { currentMonthCurrentDate } from "../../../common-data/Converts";
import { showConfirmAlert } from "../../../components-react/ReactAlert";

const LMSPMLink = ({ userInfoData, closeModal, rowData, callFunction }) => {
  const dispatch = useDispatch();
  const { userDetailsByMobile, loading: apiLoading } = useSelector(
    (state) => state.api
  );

  const { mutateAsync: updateUser, isLoading: isLoadingUser } = useUpdateForm({
    url: userUpdate,
    key: usersGetKey,
    token: userInfoData?.token,
  });

  const [selectedOption, setSelectedOption] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Member verification states
  const [isMemberVerified, setIsMemberVerified] = useState(false);
  const [isFetchingMember, setIsFetchingMember] = useState(false);
  const [isUserExist, setIsUserExist] = useState(false);
  const [isUpdateRequired, setIsUpdateRequired] = useState(null);
  const [verifiedMemberData, setVerifiedMemberData] = useState(null);

  const initialUserFormData = {
    mobile: rowData?.mobile || "",
    email: rowData?.email || "",
    name: rowData?.name || "",
    gender: rowData?.gender
      ? { label: rowData.gender, value: rowData.gender }
      : "",
    user_id: rowData?.uid || rowData?.user_id || "",
    dob: rowData?.dob || "",
  };
  const [userFormData, setUserFormData] = useState(initialUserFormData);
  const [formErrors, setFormErrors] = useState({});

  const paymentOptions = [
    {
      id: "discount",
      title: "Discount Payment Link",
      description: "Send a payment link with special discount offers",
      icon: <SendCheckIcon />,
      color: "blue",
    },
    {
      id: "deferred",
      title: "Deferred Payment Link",
      description: "Send a payment link with flexible payment schedule",
      icon: <SendIcon />,
      color: "green",
    },
  ];

  const handleOptionSelect = (optionId) => {
    setSelectedOption(optionId);
  };

  const handleNext = () => {
    if (!selectedOption) return;
    setShowForm(true);
  };

  const handleBack = () => {
    setShowForm(false);
  };

  // Reset all states and close modal
  const handleCloseModal = () => {
    // Reset form states
    setSelectedOption(null);
    setShowForm(false);
    setFormErrors({});
    setUserFormData(initialUserFormData);

    // Reset member verification states
    setIsMemberVerified(false);
    setIsFetchingMember(false);
    setIsUserExist(false);
    setIsUpdateRequired(null);
    setVerifiedMemberData(null);

    // Call parent closeModal
    closeModal();
  };

  // Handle member registration
  const onClickRegisterUser = (isUpdate) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^[A-Za-z\s]+$/;
    const yopmailRegex = /@yopmail\.com$/i;
    const mobileRegex = /^\d{10}$/;
    const today = moment().tz("Asia/Kolkata").startOf("day");

    // Validate and set field-specific errors
    const errors = {};

    if (!userFormData.mobile) {
      errors.mobile = "Mobile number is required!";
    } else if (!mobileRegex.test(userFormData.mobile)) {
      errors.mobile = "Mobile number must be 10 digits!";
    }

    if (!userFormData.name) {
      errors.name = "Name is required!";
    } else if (!nameRegex.test(userFormData.name)) {
      errors.name = "Name should only contain letters!";
    }

    if (!userFormData.email) {
      errors.email = "Email is required!";
    } else if (!emailRegex.test(userFormData.email)) {
      errors.email = "Please enter a valid email!";
    } else if (yopmailRegex.test(userFormData.email)) {
      errors.email = "Yopmail is not allowed!";
    }

    if (!userFormData.gender?.value) {
      errors.gender = "Gender is required!";
    }

    if (!userFormData.dob) {
      errors.dob = "Date of birth is required!";
    } else {
      const enteredDOB = moment
        .tz(userFormData.dob, "Asia/Kolkata")
        .startOf("day");
      const age = today.diff(enteredDOB, "years");

      if (enteredDOB.isAfter(today)) {
        errors.dob = "DOB cannot be in the future!";
      } else if (age < 10) {
        errors.dob = "Member must be at least 10 years old!";
      }
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please fill all required fields correctly!");
      return;
    }

    const userData = {
      uid: isUpdate ? userFormData?.uid : "",
      name: userFormData.name,
      mobile: userFormData.mobile,
      email: userFormData?.email,
      gender: userFormData.gender?.value,
      dob: userFormData.dob || "",
      is_web: 1,
      account_type: "member",
      otp: "otp",
      referral_code: "",
      n_token: "n_token",
    };

    if (isUpdate) {
      handleUpdateUser(userData);
    } else {
      handleRegisterUser(userData, userInfoData?.token);
    }
  };

  async function handleUpdateUser(userData) {
    async function handleUpdateUserConfirm() {
      try {
        const formdata = new FormData();

        Object.entries(userData).forEach(([key, value]) => {
          formdata.append(key, value);
        });

        const { status, message, data } = await updateUser({ data: formdata });

        if (status) {
          toast.success(message);
          setVerifiedMemberData(data[0]);
          setIsMemberVerified(true);
          setIsUserExist(false);
          setIsUpdateRequired(null);
        } else {
          toast.error(message);
        }
      } catch (error) {
        console.log(error);
      }
    }

    showConfirmAlert(
      handleUpdateUserConfirm,
      `Are you sure you want to update the member details?`
    );
  }

  const handleRegisterUser = async (userData, TOKEN) => {
    try {
      const actionResult = dispatch(registerUser({ userData, TOKEN }));
      const result = await actionResult;

      if (result?.payload?.status && result?.payload?.data) {
        toast.success("Member registered successfully!");
        setVerifiedMemberData(result.payload.data);
        setIsMemberVerified(true);
        setIsUserExist(false);
        setIsUpdateRequired(null);
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage);
      }
    } catch (error) {
      console.log("handleRegisterUser ERROR => ", error);
    }
  };

  // Handle member verification response
  useEffect(() => {
    if (isFetchingMember && userDetailsByMobile?.status) {
      const { email, gender, account_type } = userDetailsByMobile?.data?.[0];

      const isEmailInvalid = !validatemap_form_email(email);
      const isGenderMissing = !gender;

      if (account_type !== "member") {
        toast.error(
          `This Mobile Number does not belong to a member. It's a ${account_type} account!`
        );
        setIsFetchingMember(false);
        return;
      } else if (isEmailInvalid || isGenderMissing) {
        setIsUpdateRequired({
          isEmailRequired: !validatemap_form_email(email) ? email : false,
          isGenderRequired: !gender ? true : false,
        });

        const fetchedData = userDetailsByMobile?.data?.[0];
        setUserFormData({
          ...fetchedData,
          gender: fetchedData?.gender
            ? { label: fetchedData.gender, value: fetchedData.gender }
            : "",
        });

        let errorMessage = "Please update the following fields: ";
        if (isEmailInvalid) errorMessage += "Email ";
        if (isEmailInvalid && isGenderMissing) errorMessage += "and ";
        if (isGenderMissing) errorMessage += "Gender.";

        toast.error(errorMessage);
        setIsFetchingMember(false);
      } else {
        setVerifiedMemberData(userDetailsByMobile?.data?.[0]);
        setIsMemberVerified(true);
        setIsUserExist(false);
        setIsUpdateRequired(null);
        setIsFetchingMember(false);
      }
    } else if (isFetchingMember && userDetailsByMobile?.status === false) {
      setIsUserExist(true);
      toast.error(
        userDetailsByMobile?.message || "Member not found. Please register."
      );
      setIsFetchingMember(false);
    }
  }, [userDetailsByMobile, isFetchingMember]);

  // Auto-fetch on mount
  useEffect(() => {
    if (rowData?.mobile && rowData?.mobile?.length === 10) {
      dispatch(
        fetchUserDetailsByMobileData({
          userMobile: rowData.mobile,
          token: userInfoData?.token,
        })
      );
      setIsFetchingMember(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get merged row data with verified member data
  const getMergedRowData = () => {
    if (verifiedMemberData) {
      return {
        ...rowData,
        ...verifiedMemberData,
        // Preserve lead's gym and source info from rowData
        gym_id: rowData?.gym_id,
        gym_name: rowData?.gym_name,
        source_name: rowData?.source_name,
      };
    }
    return rowData;
  };

  if (showForm) {
    return (
      <div className="lms-pm-links-container">
        <div className="lms-pm-links-header">
          <button className="lms-pm-links-back-btn" onClick={handleBack}>
            <LeftArrowIcon />
            Back to Options
          </button>
          <h2 className="lms-pm-links-title">
            {selectedOption === "discount"
              ? "Discount Payment Link"
              : "Deferred Payment Link"}
          </h2>
          <p className="lms-pm-links-subtitle">
            Fill in the details to send the payment link
          </p>
        </div>

        {selectedOption === "discount" ? (
          <DiscountPaymentForm
            userInfoData={userInfoData}
            closeModal={handleCloseModal}
            rowData={getMergedRowData()}
            callFunction={callFunction}
          />
        ) : (
          <PaymentLinkForm
            userInfoData={userInfoData}
            closeModal={handleCloseModal}
            rowData={getMergedRowData()}
            callFunction={callFunction}
            paymentType={selectedOption}
          />
        )}
      </div>
    );
  }

  // Loading state while fetching member details
  if (apiLoading || isFetchingMember) {
    return (
      <div className="lms-pm-links-container">
        <div className="lms-pm-links-header">
          <h2 className="lms-pm-links-title">Verifying Member</h2>
          <p className="lms-pm-links-subtitle">
            Please wait while we verify the member details...
          </p>
        </div>
        <div className="lms-pm-links-loading">
          <div className="lms-pm-links-spinner"></div>
          <p>Fetching member information...</p>
        </div>
        <div className="lms-pm-links-actions">
          <button
            className="lms-pm-links-btn lms-pm-links-btn-secondary"
            onClick={handleCloseModal}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Member update/registration form
  if (isUserExist || isUpdateRequired) {
    return (
      <div className="lms-pm-links-container">
        <div className="lms-pm-links-header">
          <h2 className="lms-pm-links-title">
            {isUpdateRequired ? "Update Member Details" : "Register New Member"}
          </h2>
          <p className="lms-pm-links-subtitle">
            {isUpdateRequired
              ? "Please update the missing information to continue"
              : "This member is not registered. Please fill in the details to register."}
          </p>
        </div>

        <div className="lms-pm-links-member-form">
          <div className="lms-pm-links-form-grid">
            <div className="lms-pm-links-form-field">
              <label className="lms-pm-links-form-label">
                Mobile Number <span className="required">*</span>
              </label>
              <input
                type="text"
                className={`lms-pm-links-form-input lms-pm-links-form-input-disabled ${
                  formErrors?.mobile ? "formError" : ""
                }`}
                value={userFormData.mobile}
                disabled
              />
              <span className="error-label">{formErrors?.mobile || ""}</span>
            </div>

            <div className="lms-pm-links-form-field">
              <label className="lms-pm-links-form-label">
                Name <span className="required">*</span>
              </label>
              <input
                type="text"
                className={`lms-pm-links-form-input ${
                  formErrors?.name ? "formError" : ""
                }`}
                placeholder="Enter Name"
                value={userFormData.name}
                onChange={(e) => {
                  setUserFormData({ ...userFormData, name: e.target.value });
                  if (formErrors?.name) {
                    setFormErrors({ ...formErrors, name: "" });
                  }
                }}
              />
              <span className="error-label">{formErrors?.name || ""}</span>
            </div>

            <div className="lms-pm-links-form-field">
              <label className="lms-pm-links-form-label">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                className={`lms-pm-links-form-input ${
                  formErrors?.email ? "formError" : ""
                }`}
                style={{ textTransform: "lowercase" }}
                placeholder="Enter Email"
                value={userFormData.email}
                onChange={(e) => {
                  setUserFormData({ ...userFormData, email: e.target.value });
                  if (formErrors?.email) {
                    setFormErrors({ ...formErrors, email: "" });
                  }
                }}
              />
              <span className="error-label">{formErrors?.email || ""}</span>
            </div>

            <div className="lms-pm-links-form-field">
              <label className="lms-pm-links-form-label">
                Gender <span className="required">*</span>
              </label>
              <div
                className={`commonWrapper ${
                  formErrors?.gender ? "formError" : ""
                }`}
              >
                <SelectDropdown
                  options={gender.slice(0, -1)}
                  selectedOption={userFormData.gender}
                  setSelectedOption={(selectedValue) => {
                    setUserFormData({
                      ...userFormData,
                      gender: selectedValue,
                    });
                    if (formErrors?.gender) {
                      setFormErrors({ ...formErrors, gender: "" });
                    }
                  }}
                  name="gender"
                />
              </div>
              <span className="error-label">{formErrors?.gender || ""}</span>
            </div>

            <div className="lms-pm-links-form-field">
              <label className="lms-pm-links-form-label">
                Date of Birth <span className="required">*</span>
              </label>
              <input
                type="date"
                className={`lms-pm-links-form-input ${
                  userFormData.dob ? "date-has-value" : ""
                } ${formErrors?.dob ? "formError" : ""}`}
                value={userFormData.dob}
                onChange={(e) => {
                  setUserFormData({ ...userFormData, dob: e.target.value });
                  if (formErrors?.dob) {
                    setFormErrors({ ...formErrors, dob: "" });
                  }
                }}
                max={currentMonthCurrentDate}
              />
              <span className="error-label">{formErrors?.dob || ""}</span>
            </div>
          </div>
        </div>

        <div className="lms-pm-links-actions">
          <button
            className="lms-pm-links-btn lms-pm-links-btn-secondary"
            onClick={handleCloseModal}
          >
            Cancel
          </button>
          <button
            className="lms-pm-links-btn lms-pm-links-btn-primary"
            onClick={() => onClickRegisterUser(isUpdateRequired ? true : false)}
            disabled={isLoadingUser}
          >
            {isLoadingUser ? (
              <div className="lms-pm-links-spinner-small"></div>
            ) : isUpdateRequired ? (
              "Update & Continue"
            ) : (
              "Register & Continue"
            )}
          </button>
        </div>
      </div>
    );
  }

  // Main payment options view (only shown after member is verified)

  // Main payment options view (only shown after member is verified)
  return (
    <div className="lms-pm-links-container">
      <div className="lms-pm-links-header">
        <h2 className="lms-pm-links-title">Send Payment Link</h2>
        <p className="lms-pm-links-subtitle">
          Choose the type of payment link you want to send
        </p>
      </div>

      <div className="lms-pm-links-member-details">
        <div className="lms-pm-links-member-header">
          <h3 className="lms-pm-links-member-title">Member Details</h3>
          {isMemberVerified && (
            <span className="lms-pm-links-verified-badge">Verified</span>
          )}
        </div>
        <div className="lms-pm-links-member-info-grid">
          <div className="lms-pm-links-info-item">
            <span className="lms-pm-links-info-label">Name</span>
            <span className="lms-pm-links-info-value">
              {verifiedMemberData?.name || rowData?.name || "N/A"}
            </span>
          </div>
          <div className="lms-pm-links-info-item">
            <span className="lms-pm-links-info-label">Email</span>
            <span className="lms-pm-links-info-value">
              {verifiedMemberData?.email || rowData?.email || "N/A"}
            </span>
          </div>
          <div className="lms-pm-links-info-item">
            <span className="lms-pm-links-info-label">Mobile</span>
            <span className="lms-pm-links-info-value">
              {verifiedMemberData?.mobile || rowData?.mobile || "N/A"}
            </span>
          </div>
          <div className="lms-pm-links-info-item">
            <span className="lms-pm-links-info-label">Source</span>
            <span className="lms-pm-links-info-value">
              {rowData?.source_name || "N/A"}
            </span>
          </div>
        </div>
      </div>

      <div className="lms-pm-links-options-grid">
        {paymentOptions.map((option) => (
          <div
            key={option.id}
            className={`lms-pm-links-option-card ${
              selectedOption === option.id
                ? "lms-pm-links-option-card-selected"
                : ""
            } lms-pm-links-option-card-${option.color}`}
            onClick={() => handleOptionSelect(option.id)}
          >
            <div className="lms-pm-links-option-icon-wrapper">
              <div
                className={`lms-pm-links-option-icon lms-pm-links-option-icon-${option.color}`}
              >
                {option.icon}
              </div>
            </div>

            <div className="lms-pm-links-option-content">
              <h3 className="lms-pm-links-option-title">{option.title}</h3>
              <p className="lms-pm-links-option-description">
                {option.description}
              </p>
            </div>

            <div className="lms-pm-links-option-radio">
              <div
                className={`lms-pm-links-radio-circle ${
                  selectedOption === option.id
                    ? "lms-pm-links-radio-circle-checked"
                    : ""
                }`}
              >
                {selectedOption === option.id && (
                  <div className="lms-pm-links-radio-dot"></div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="lms-pm-links-actions">
        <button
          className="lms-pm-links-btn lms-pm-links-btn-secondary"
          onClick={handleCloseModal}
        >
          Cancel
        </button>
        <button
          className={`lms-pm-links-btn lms-pm-links-btn-primary ${
            !selectedOption ? "lms-pm-links-btn-disabled" : ""
          }`}
          onClick={handleNext}
          disabled={!selectedOption}
        >
          Next
          <SendIcon />
        </button>
      </div>
    </div>
  );
};

export default LMSPMLink;
